import { createContext, useState, useMemo, useEffect } from 'react';
import { getQAConfig } from './utils';
import { getNewLoadDetails, getNeeNorDetails } from '../../apiCalls';

export const QAStoreContext = createContext(null);

const QAStoreProvider = ({ children }) => {
  const STAT_VALUE_WARNING_AMOUNT = 150000;

  const initialConfig = useMemo(() => {
    return getQAConfig();
  }, []);

  const [initialDataFetched, setInitialDataFetched] = useState(false);

  const [consigneeData, setConsigneeData] = useState({});
  const [consignorData, setConsignorData] = useState({});
  const [invoiceCurrency, setInvoiceCurrency] = useState('');
  const [potentialLiabilityGBP, setPotentialLiabilityGBP] = useState(0);
  const [currentConfig, setCurrentConfig] = useState({ ...initialConfig });
  const [newLoadQaVat, setNewLoadQaVat] = useState({
    id: null,
    vatMessage: '',
    vatValid: true,
  });
  const [nonHybridPortModelID, setNonHybridPortModelID] = useState(0);
  const [itemTotals, setItemTotals] = useState({
    packageCount: 0,
    grossMass: 0,
    netMass: 0,
    value: 0,
    valueGbp: 0,
    dutyLiabilityGbp: 0,
    vatLiabilityGbp: 0,
    itemRows: 0,
  });
  const [statValueWarning, setStatValueWarning] = useState(false);
  const [confirmDutyRequired, setConfirmDutyRequired] = useState(false); // CABS-2707 - specifies if user must confirm dutiable load via customs MoP modal

  // functions
  const fetchPortModelFromPortID = async (portID) => {
    try {
      const data = await getNewLoadDetails('portprocess', portID);
      setNonHybridPortModelID(parseInt(data[0]?.portProcessId || data[0]?.exportPortProcessId));
    } catch (error) {
      console.error('Error fetching port models data from getNewLoadDetails for portID:  ', portID, ' | ', error);
    }
  };

  const fetchConsigneeData = async (consigneeID) => {
    try {
      if (consigneeID) {
        //get ADDRESS details for a given Consignee. This data will be used when the BP is not to be displayed
        const data = await getNeeNorDetails(consigneeID);
        setConsigneeData(data[0] || {});
      }
    } catch (error) {
      console.error('Error fetching consignee data from getNeeNorDetails for consignee:  ', consigneeID, ' | ', error);
    }
  };

  const fetchConsignorData = async (consignorID) => {
    try {
      if (consignorID) {
        //get ADDRESS details for a given Consignor. This data will be used when the BP is not to be displayed
        const data = await getNeeNorDetails(consignorID);
        setConsignorData(data[0]);
      }
    } catch (error) {
      console.error('Error fetching consignor data from getNeeNorDetails for consignor:  ', consignorID, ' | ', error);
    }
  };

  useEffect(() => {
    if (itemTotals.valueGbp) {
      setStatValueWarning(itemTotals.valueGbp > STAT_VALUE_WARNING_AMOUNT);
    }
  }, [itemTotals]);

  const resetQAStore = () => {
    setInitialDataFetched(false);
    setConsigneeData({});
    setConsignorData({});
    setInvoiceCurrency('');
    setPotentialLiabilityGBP(0);
    setCurrentConfig({ ...initialConfig });
    setNewLoadQaVat({ id: null, vatMessage: '', vatValid: true });
    setNonHybridPortModelID(0);
    setItemTotals({
      packageCount: 0,
      grossMass: 0,
      netMass: 0,
      value: 0,
      valueGbp: 0,
      dutyLiabilityGbp: 0,
      vatLiabilityGbp: 0,
      itemRows: 0,
    });
    setStatValueWarning(false);
    setConfirmDutyRequired(false);
  };

  const store = useMemo(
    () => ({
      initialDataFetched: [initialDataFetched, setInitialDataFetched],
      consigneeData: [consigneeData, setConsigneeData],
      consignorData: [consignorData, setConsignorData],
      invoiceCurrency: [invoiceCurrency, setInvoiceCurrency],
      currentConfig: [currentConfig, setCurrentConfig],
      newLoadQaVat: [newLoadQaVat, setNewLoadQaVat],
      nonHybridPortModelID: [nonHybridPortModelID, setNonHybridPortModelID],
      itemTotals: [itemTotals, setItemTotals],
      potentialLiabilityGBP: [potentialLiabilityGBP, setPotentialLiabilityGBP],
      confirmDutyRequired: [confirmDutyRequired, setConfirmDutyRequired],
      statValueWarning,
      resetQAStore,
      fetchPortModelFromPortID,
      fetchConsigneeData,
      fetchConsignorData,
    }),
    [
      initialDataFetched,
      consigneeData,
      consignorData,
      currentConfig,
      invoiceCurrency,
      newLoadQaVat,
      nonHybridPortModelID,
      itemTotals,
      confirmDutyRequired,
      resetQAStore,
    ]
  );

  return <QAStoreContext.Provider value={store}>{children}</QAStoreContext.Provider>;
};

export default QAStoreProvider;
